import {
  faEllipsisH,
  faLock,
  faHardDrive,
  faCloudArrowDown,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames';
import * as React from 'react';
import { AspectRatio, Button } from '@readcloud/ui';
import { TauriImage } from '@readcloud/fetch-wrapper';
import { MdSettings } from 'react-icons/md';

export function TileImage(props: {
  title: string;
  tileType: string;
  src: { url: string } | 'NoImage';
  alt: string;
  locked?: boolean;
  onClick?: () => void;
  /** Define what happens when the "..." is clicked at the bottom of the tile. If this is undefined, then the button won't show */
  onMoreClick?: () => void;
  size?: 'small' | 'medium' | 'large';
  hideTitle?: boolean;
  /** Whether the book content is available offline (cached locally) */
  isAvailableOffline?: boolean;
  /** Whether to show the offline indicator (only in Tauri environment) */
  showOfflineIndicator?: boolean;
  /** Whether the app is currently offline */
  isOffline?: boolean;
  onContentSheetClick?: ()=>void;
  isMyContent: boolean;
}) {
  const [noImage, setNoImage] = React.useState<boolean>(false);
  React.useEffect(() => {
    setNoImage(false);
  }, [props.src]);
  return (
    <div className="rc-relative rc-max-h-min rc-max-w-min rc-mb-6">
      <div
        className={cn(
          // Add a slight top border so that some tiles with similar backgrounds don't just blend in.
          'rc-border-0 rc-border-t rc-border-slate-100 rc-border-solid',
          ' rc-shadow-lg rc-bg-white rc-rounded-lg rc-overflow-hidden',
          // size - default to medium
          props.size === 'small'
            ? ' rc-w-20  lg:rc-w-28 '
            : props.size === 'large'
            ? ' rc-w-44  lg:rc-w-44'
            : ' rc-w-28  lg:rc-w-36',
          // This is to prevent any highlighting when the user drags or double clicks (which shows selection/highlight styles)
          '.select-none'
        )}
        // Keep the onClick handler on an element that doesn't have hover styling. Theres an iOS bug where the element will then require two clicks for the handler to fire.
        onClick={!props.locked ? props.onClick : undefined}
      >
        {noImage || props.src === 'NoImage' ? (
          <AspectRatio ratio={3 / 4}>
            <div
              className={cn(
                'rc-p-3 rc-py-6 rc-text-xs rc-text-center rc-h-full rc-w-full',
                // Increase font size a bit on desktop
                'lg:rc-text-md'
              )}
            >
              <div>{props.title}</div>
            </div>
          </AspectRatio>
        ) : (
          <>
            <AspectRatio ratio={3 / 4}>
              <TauriImage
                alt={props.alt}
                loading="lazy"
                className="rc-h-full rc-w-full rc-object-cover rc-scale-[1.005]"
                src={props.src.url}
                onError={() => setNoImage(true)}
              />
            </AspectRatio>
          </>
        )}
        {!props.hideTitle && (
          <div
            className={cn(
              props.size === 'large' ? 'rc-h-16' : 'rc-h-12',
              'rc-leading-4 rc-p-2'
            )}
          >
            <span
              className={cn(
                props.size === 'large' ? 'rc-text-md' : 'rc-text-xs',
                ' rc-w-full rc-line-clamp-2'
              )}
            >
              {props.title}
            </span>
          </div>
        )}
        <div
          className={cn(
            // This element is what the user will interact with. Because of this extra layer, we can show a larger variety of effects upon interactions.
            'rc-absolute rc-top-0 rc-left-0 rc-w-full rc-h-full rc-border-none rc-overflow-hidden rc-rounded-lg rc-bg-slate-50',
            // Interaction style
            !props.locked &&
              props.onClick &&
              '.cursor-pointer hover:.opacity-20',
            // "Locked" style
            props.locked ? 'rc-opacity-50' : 'rc-opacity-0'
          )}
        />
        {props.locked && (
          <div>
            <FontAwesomeIcon
              className="rc-absolute rc-top-0 rc-bottom-0 rc-left-0 rc-right-0 rc-m-auto"
              size="4x"
              icon={faLock}
            />
          </div>
        )}
        {/* Offline overlay for non-cached content */}
        {props.isOffline &&
          props.showOfflineIndicator &&
          !props.isAvailableOffline &&
          !props.locked && (
            <>
              <div className="rc-absolute rc-top-0 rc-left-0 rc-w-full rc-h-full rc-bg-slate-500/60 rc-rounded-lg" />
              <div className="rc-absolute rc-top-0 rc-bottom-0 rc-left-0 rc-right-0 rc-flex rc-flex-col rc-items-center rc-justify-center rc-text-white">
                <FontAwesomeIcon size="2x" icon={faCloudArrowDown} />
                <span className="rc-text-xs rc-mt-1 rc-font-medium">
                  Unavailable offline
                </span>
              </div>
            </>
          )}
      </div>
      <div
        className={cn(
          'rc-text-xs rc-absolute rc-w-full rc-text-stone-500 rc-flex rc-justify-between rc-items-center rc-pt-1'
        )}
      >
        <span className="rc-text-ellipsis rc-overflow-hidden rc-flex rc-items-center rc-gap-1">
          {props.tileType}
          {/* Offline availability indicator */}
          {props.showOfflineIndicator && props.isAvailableOffline && (
            <FontAwesomeIcon
              className="rc-text-primary"
              size="xs"
              icon={faHardDrive}
              title="Available offline"
            />
          )}
        </span>
        <div className='rc-flex rc-items-center'>
          {
            props.onContentSheetClick && props.isMyContent && (
              <Button
                size="icon"
                className="rc-h-4 rc-w-8"
                variant="link"
                onClick={props.onContentSheetClick}
              >
                <MdSettings />
              </Button>
            )
          }
          
          <div>
            {props.onMoreClick && (
              <Button
                size="icon"
                className="rc-h-4 rc-w-8"
                variant="link"
                onClick={props.onMoreClick}
              >
                <FontAwesomeIcon size="sm" icon={faEllipsisH} />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
